/**
 * ContractsPrint — impression du rapport People « Contracts ».
 * Réplique HrContractsPage : bloc résumé (open contracts) + table complète
 * (1 ligne par contrat) avec total. Réutilise les mêmes dérivés
 * (effective yearly = salary × nb_sal ; yearly @100% = effective ÷ activity).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listHrContracts, listHrPeople } from '../../../api/hr';
import { formatNumber, formatDate } from '../../../utils/format';
import PrintReportHeader from './PrintReportHeader';
import './PeoplePrint.css';

const f0 = (v) => formatNumber(v, 0);
const effectiveYearly = (c) => Number(c.monthly_salary || 0) * Number(c.nb_of_salary || 0);
const yearly100 = (c) => {
  const ar = Number(c.activity_rate || 0);
  return ar > 0 ? effectiveYearly(c) / ar : 0;
};

export default function ContractsPrint({ activeTenant, year, editedAt, onDataReady }) {
  const { t } = useTranslation();
  const tenantId = activeTenant?.id;
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!tenantId) return undefined;
    Promise.all([
      listHrContracts(tenantId).then(r => r.data || []),
      listHrPeople(tenantId).then(r => r.data || []),
    ]).then(([contracts, people]) => {
      if (!alive) return;
      setData({ contracts: contracts || [], people: people || [] });
      if (onDataReady) onDataReady('contracts');
    }).catch(() => { if (onDataReady) onDataReady('contracts'); });
    return () => { alive = false; };
  }, [tenantId]); // eslint-disable-line

  const model = useMemo(() => {
    if (!data) return null;
    const pIdx = {};
    for (const p of data.people) pIdx[p.id] = p;
    const rows = data.contracts.map(c => {
      const p = pIdx[c.person_id];
      return {
        ...c,
        first: p ? (p.first_name || '') : '',
        last: p ? (p.last_name || '') : '',
        lastSort: p ? (p.last_name || '').toLowerCase() : '',
        eff: effectiveYearly(c),
        y100: yearly100(c),
      };
    }).sort((a, b) => a.lastSort.localeCompare(b.lastSort, 'fr'));

    const totals = rows.reduce((acc, c) => ({
      monthly: acc.monthly + Number(c.monthly_salary || 0),
      activity: acc.activity + Number(c.activity_rate || 0),
      eff: acc.eff + c.eff,
      y100: acc.y100 + c.y100,
    }), { monthly: 0, activity: 0, eff: 0, y100: 0 });

    const open = rows.filter(c => c.end_date == null);
    const summary = {
      count: open.length,
      monthly: open.reduce((a, c) => a + Number(c.monthly_salary || 0), 0),
      eff: open.reduce((a, c) => a + c.eff, 0),
      y100: open.reduce((a, c) => a + c.y100, 0),
    };
    return { rows, totals, summary };
  }, [data]);

  if (!model) return <div className="ppl-print__section"><p>{t('common:status.loading')}</p></div>;

  const { rows, totals, summary } = model;

  return (
    <section className="ppl-print__section ppl-print__section--wide">
      <PrintReportHeader category="People" title="Contracts" year={year} tenant={activeTenant} editedAt={editedAt} />

      {/* Résumé — contrats ouverts (end date vide) */}
      <table className="ppl-print__table" style={{ marginBottom: 10 }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Open contracts control</th>
            <th>Persons</th>
            <th>Monthly salaries</th>
            <th>Effective yearly</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ textAlign: 'left' }}>End date empty = open</td>
            <td><strong>{summary.count}</strong></td>
            <td>{f0(summary.monthly)}</td>
            <td>{f0(summary.eff)}</td>
          </tr>
        </tbody>
      </table>

      {/* Table complète des contrats */}
      <table className="ppl-print__table ppl-print__table--total-once">
        <thead>
          <tr>
            <th style={{ textAlign: 'left' }}>Person</th>
            <th style={{ textAlign: 'left' }}>Title</th>
            <th style={{ textAlign: 'left' }}>Department</th>
            <th style={{ textAlign: 'left' }}>Start</th>
            <th style={{ textAlign: 'left' }}>End</th>
            <th>Salary / month</th>
            <th>Nb sal.</th>
            <th>Activity rate</th>
            <th>Effective yearly</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(c => (
            <tr key={c.id}>
              <td style={{ textAlign: 'left' }}>{c.first} {c.last ? c.last.toUpperCase() : ''}</td>
              <td style={{ textAlign: 'left' }}>{c.title || '—'}</td>
              <td style={{ textAlign: 'left' }}>{c.department ? <span className="ppl-print__badge">{c.department}</span> : '—'}</td>
              <td className="nowrap" style={{ textAlign: 'left' }}>{c.start_date ? formatDate(c.start_date) : '—'}</td>
              <td className="nowrap" style={{ textAlign: 'left' }}>{c.end_date ? formatDate(c.end_date) : '—'}</td>
              <td>{f0(c.monthly_salary)}</td>
              <td>{c.nb_of_salary}</td>
              <td>{c.activity_rate != null ? `${formatNumber(Number(c.activity_rate) * 100, 0)}%` : '—'}</td>
              <td>{f0(c.eff)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ textAlign: 'left' }} colSpan={5}><strong>Total ({rows.length})</strong></td>
            <td><strong>{f0(totals.monthly)}</strong></td>
            <td />
            <td><strong>{totals.activity.toFixed(1)}</strong></td>
            <td><strong>{f0(totals.eff)}</strong></td>
          </tr>
        </tfoot>
      </table>
    </section>
  );
}
